import { useEffect, useState } from 'react';
import type { DmListPosition } from '../../../services/dmNotifications';
import { apiErrorStatus } from '../../../utils/apiError';
import {
    listPositionLabel,
    rememberListPosition,
    type KnownListPosition,
    type ListPositionLabel
} from './listPosition';

const HTTP_NOT_FOUND = 404;

export type PatientViewStatus = 'loading' | 'notFound' | 'error' | 'ready';

export interface PatientNeighbours {
    previousPatientId: string | null;
    nextPatientId: string | null;
}

const NO_NEIGHBOURS: PatientNeighbours = { previousPatientId: null, nextPatientId: null };

// Once the patient drops out of the filtered set the endpoint reports no neighbours; keep the last known.
export function useLastKnownNeighbours(latest: PatientNeighbours | undefined): PatientNeighbours {
    const [known, setKnown] = useState<PatientNeighbours>(NO_NEIGHBOURS);
    useEffect(() => {
        if (!latest) return;
        setKnown((prev) => ({
            previousPatientId: latest.previousPatientId ?? prev.previousPatientId,
            nextPatientId: latest.nextPatientId ?? prev.nextPatientId
        }));
    }, [latest]);
    return {
        previousPatientId: latest?.previousPatientId ?? known.previousPatientId,
        nextPatientId: latest?.nextPatientId ?? known.nextPatientId
    };
}

// Like the neighbours: once the patient drops out, show where it was rather than nothing.
export function useListPositionLabel(latest: DmListPosition | undefined): ListPositionLabel | null {
    const [lastKnown, setLastKnown] = useState<KnownListPosition | null>(null);
    useEffect(() => {
        setLastKnown((prev) => rememberListPosition(prev, latest));
    }, [latest]);
    return listPositionLabel(latest, lastKnown);
}

// Data on hand wins: a failed background refetch shouldn't blank a patient being worked.
export function patientViewStatus(
    hasData: boolean,
    isError: boolean,
    error: unknown
): PatientViewStatus {
    if (hasData) return 'ready';
    if (!isError) return 'loading';
    return apiErrorStatus(error) === HTTP_NOT_FOUND ? 'notFound' : 'error';
}

export function stepTo(
    patientId: string | null,
    goToPatient: (id: string) => void
): (() => void) | undefined {
    return patientId ? () => goToPatient(patientId) : undefined;
}
